import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, TextField, Button, IconButton, Avatar } from "@mui/material"; // Material-UI components
import { ArrowBackIos, Call, PhotoLibraryRounded, SendRounded } from "@mui/icons-material"; // Material-UI icons
import { format } from "date-fns"; // Date formatting utility
import styled from 'styled-components'; // For styled components
import { useSwipeable } from "react-swipeable"; // Library for swipe gestures

import { useNavigate, useLocation } from "react-router-dom"; // React Router hooks
import ChatHeader from "./ChatHeader";
import LoadingScreenBlackLogo from "../../common/LoadingProgressBar";
import { fetch_chat, fetch_info, send_user_conversation } from "../../apiservices/ApiService";

const Fsn2CoachChat = () => {

    const location = useLocation(); // Get the current route location
    const navigate = useNavigate(); // Navigation hook for route changes

    // State variables
    const [messages, setMessages] = useState([]); // Stores chat messages
    const [isFetchingMessages, setIsFetchingMessages] = useState(false); // Tracks fetching state for new messages
    const [isFetchingInfo, setIsFetchingInfo] = useState(false); // Tracks fetching state for coach info
    const [newMessage, setNewMessage] = useState(""); // Stores the new message text
    const [messageId, setMessageId] = useState(0); // Tracks the latest message ID
    const [coachData, setCoachData] = useState(null); // Stores coach details
    const [referenceMessage, setReferenceMessage] = useState(null); // Stores reference message for reply
    const [isLoading, setIsLoading] = useState(false); // Loading state for actions
    const [loadingMessage, setLoadingMessage] = useState("Sending message, please wait..."); // Loading message text
    const chatRef = useRef(null); // Reference to the chat container for scrolling

    // Navigate to the cart page
    const cartPageNavigation = () => {
        const currentQueryParams = window.location.search; // Get current query parameters
        window.location.href = `/coach${currentQueryParams}`; // Redirect to coach cart page
    };

    // Swipeable bubble for enabling swipe gestures on chat bubbles
    const SwipeableBubble = ({ children, onSwipeRight }) => {
        const [translateX, setTranslateX] = useState(0); // X-axis translation for swipe animation
        const [isSwiped, setIsSwiped] = useState(false); // State to handle swipe completion
        const handlers = useSwipeable({
            onSwiping: (eventData) => {
                if (eventData.dir === "Right") {
                    setTranslateX(eventData.deltaX); // Update X-axis translation while swiping
                }
            },
            onSwipedRight: () => {
                setIsSwiped(true); // Mark swipe as completed
                setTimeout(() => {
                    onSwipeRight(); // Trigger reply action
                    setIsSwiped(false); // Reset swipe state
                    setTranslateX(0); // Reset translation
                }, 300); // Match animation duration
            },
            onSwiped: () => {
                setTranslateX(0); // Reset translation if swipe is incomplete
            },
            preventDefaultTouchmoveEvent: true,
            trackMouse: true,
        });

        return (
            <div
                {...handlers}
                style={{
                    transform: `translateX(${translateX}px)`, // Apply X-axis translation
                    transition: isSwiped ? "transform 0.3s ease-out" : "none", // Smooth transition for swipe
                    position: "relative",
                }}
            >
                {children}
            </div>
        );
    };

    // Styled components for chat bubbles
    const IncomingBubble = styled.div`
        background-color: #FF9800;
        color: white;
        max-width: 75%;
        padding: 12px;
        border-radius: 16px;
        margin-bottom: 10px;
        display: inline-block;
        text-align: left;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1); // Optional: Add shadow for better bubble effect
    `;

    const OutgoingBubble = styled.div`
        background-color: #771615;
        color: white;
        max-width: 75%;
        padding: 12px;
        border-radius: 16px;
        margin-bottom: 10px;
        display: inline-block;
        text-align: left;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1); // Optional: Add shadow for better bubble effect
    `;
    //  // Fetch coach information and initial messages
    const fetchCoachInfo = async () => {

        if (isFetchingInfo) return; // Avoid multiple fetch calls
        setIsFetchingInfo(true);

        console.error("fetch_info : ");
        const response = await fetch_info({ coachingtype: "coach" });

        console.error("fetch_info : ", response);

        if (response.code === 200 && response.data != null) {
            if (response && response.data) {
                setCoachData(response.data); // Update coach data
                fetchNewMessages(); // Fetch initial messages
            }
        }

        setIsFetchingInfo(false); // Reset fetching state
    };

    // Fetch chat messages based on direction (new/old)
    const fetchChatMessages = async (direction) => {
        var latestMessageId = messageId; // Initialize latest message ID

        if (Array.isArray(messages) && messages.length > 0) {
            const sortedMessages = [...messages].sort((a, b) => a.messageId - b.messageId);
            latestMessageId =
                direction === "new"
                    ? sortedMessages[messages.length - 1]?.messageId
                    : sortedMessages[0]?.messageId;
        }

        // Store values in localStorage
        localStorage.setItem("serverId", latestMessageId);
        localStorage.setItem("clanId", "1");
        localStorage.setItem("direction", direction);

        const serverId = localStorage.getItem("serverId");
        const clanId = localStorage.getItem("clanId");
        const direction1 = localStorage.getItem("direction");

        try {
            console.error("fetch_chat : ");
            const data = await fetch_chat({ serverId: serverId, clanId: clanId, direction: direction1 });

            if (data && data.data && data.data.cards) {
                return { messages: data.data.cards, latestMessageId };
            } else {
                return { messages: [], latestMessageId };
            }
        } catch (error) {
            console.error("Error fetching chat:", error);
            return { messages: [], latestMessageId };
        }
    };

    // Fetch new messages
    const fetchNewMessages = async () => {
        if (isFetchingMessages) return;
        setIsFetchingMessages(true);

        const data = await fetchChatMessages("new");

        if (data.messages.length > 0) {
            if (data.latestMessageId === 0)
                setMessages(data.messages);
            else
                setMessages((prevMessages) => [...prevMessages, ...data.messages]);
            scrollToBottom(); // Scroll to the latest message
            setMessageId(data.latestMessageId);
        }
        setIsFetchingMessages(false);
    };

    // Fetch older messages when scrolling to the top
    const fetchOlderMessages = async () => {
        if (isFetchingMessages) return;
        setIsFetchingMessages(true);

        const data = await fetchChatMessages("old");

        if (data.messages.length > 0) {
            setMessages((prevMessages) => [...data.messages, ...prevMessages]);
            setMessageId(data.latestMessageId);
        }
        setIsFetchingMessages(false);
    };

    // Scroll event handler to fetch older messages
    const handleScroll = () => {
        if (chatRef.current.scrollTop === 0) {
            fetchOlderMessages(); // Fetch older messages if scrolled to the top
        }
    };

    // Send message or image
    const sendMessage = async (fromImage, imageUrl) => {
        if (fromImage && imageUrl === "") return; // Avoid sending if no image
        else if (!fromImage && newMessage === "") return; // Avoid sending if no text
        setIsLoading(true);
        setLoadingMessage(fromImage ? "Sending image, please wait..." : "Sending message, please wait...");

        // var currentQueryParams = window.location.search;


        const timestamp = Date.now();
        let messageData = JSON.stringify([
            {
                message: newMessage,
                imageUrl: imageUrl,
                messageTimestamp: timestamp,
                aspect: 0,
            },
        ]);
    
        if (referenceMessage != null) {
            messageData = JSON.stringify([
                {
                    message: newMessage,
                    imageUrl: imageUrl,
                    messageTimestamp: timestamp,
                    aspect: 0,
                    referenceMessage: referenceMessage,
                },
            ]);
        }

        
        


        setReferenceMessage(null); // Clear reference message
        setNewMessage(""); // Clear input field

        try {


            console.error("send_user_conversation : ");
            const response = await send_user_conversation({ goqiiCoachId: coachData.coachPlanInfo.coachId, chatData: messageData});

            if (response.code==200) {
                fetchNewMessages(); // Refresh messages after sending
            } else {
                console.error("Failed to send message:");
            }
        } catch (error) {
            console.error("Error sending message:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle image selection and upload
    const handleImageSelect = async (event) => {
        setIsLoading(true);
        setLoadingMessage("Uploading image, please wait...");
        const file = event.target.files[0]; // Get the selected file
        if (file) {
            const formData = new FormData();
            formData.append("image", file);

            try {
                const BASE_URL = process.env.REACT_APP_BASE_URL;

                const response = await fetch(`https://apiv4.goqii.com/user/upload_endoded_image`, {
                  method: "POST",
                  body: formData,
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data && data.data && data.data.image) {
                        sendMessage(true, data.data.image); // Send image message
                    } else {
                        setIsLoading(false);
                        console.error("Upload failed: data null or image null");
                    }
                } else {
                    console.error("Upload failed:", response.statusText);
                    setIsLoading(false);
                }
            } catch (error) {
                console.error("Upload error:", error);
                setIsLoading(false);
            }
        }
    };

    // Scroll to the bottom of the chat
    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); // Smooth scroll
        }, 50);
    };

    useEffect(() => {
        fetchCoachInfo(); // Fetch coach info on mount
        const intervalId = setInterval(() => {
            fetchNewMessages(); // Fetch new messages every 10 seconds
        }, 10000);
        return () => clearInterval(intervalId); // Cleanup interval on unmount
    }, [messageId]);

    useEffect(() => {
        const chatContainer = chatRef.current;
        if (chatContainer) {
            chatContainer.addEventListener("scroll", handleScroll); // Add scroll event listener
        }
        return () => {
            if (chatContainer) {
                chatContainer.removeEventListener("scroll", handleScroll); // Cleanup listener on unmount
            }
        };
    }, [messages]);

    // Handle swipe reply action
    const handleSwipeReply = (message) => {
        setReferenceMessage({
            text: message.cardData[0].data.text,
            aspect: "1",
            messageTimestamp: message.cardData[0].messageTimestamp,
            imageUrl: message.cardData[0].data.imageUrl,
            image: message.cardData[0].data.imageUrl,
            OnTap: { FAI: { messageId: message.cardData[0].messageId, type: "user" } },
        });
    };

    // Group messages by date for display
    const groupMessagesByDate = (messages) => {
        return messages.reduce((acc, message) => {
            const messageDate = format(new Date(message.messageTimestamp), "yyyy-MM-dd");
            if (!acc[messageDate]) {
                acc[messageDate] = [];
            }
            acc[messageDate].push(message);
            return acc;
        }, {});
    };

    const messagesEndRef = useRef(null); // Reference for scrolling to bottom
    var groupedMessages = groupMessagesByDate(messages); // Group messages by date

    // Handle back button navigation
    const handleBackButtonPress = () => {

        // Logic for handling back only on "/doctor-consultation"
        if (window.Android) {
            // If on Android, call the Android bridge method
            window.Android.onBackPressed();
        } else {
            navigate(-1); // Navigate back in history
        }
    };

    if (!coachData || !messages.length) {
        return null; // Render nothing if no coach data or messages
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100vh",
                backgroundColor: "#f5f5f5",
                position: "relative",
            }}
        >
            <ChatHeader
                coachData={coachData}
                cartPageNavigation={cartPageNavigation}
                handleBackButtonPress={handleBackButtonPress}
            />
            {/* Chat Messages */}
            <Box
                ref={chatRef}
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "10px",
                    backgroundColor: "rgba(255, 152, 0, 0.11)",
                    boxShadow: "0 0 5px rgba(0, 0, 0, 0.2)",
                }}
            >
                {Object.entries(groupedMessages).map(([date, messages]) => (
                    <React.Fragment key={date}>
                        <Typography
                            sx={{
                                textAlign: "center",
                                margin: "10px 0",
                                color: "#999",
                                fontSize: "12px",
                            }}
                        >
                            {format(new Date(date), "MMMM dd, yyyy")}
                        </Typography>
                        {messages.map((message, index) => (
                            <SwipeableBubble
                                key={index}
                                onSwipeRight={() => handleSwipeReply(message)}
                            >
                                <Box
                                    sx={{
                                        marginBottom: "10px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: message.senderType === "coach" ? "flex-start" : "flex-end",
                                    }}
                                >
                                    {message.senderType === "coach" ? (
                                        <IncomingBubble>
                                            {message.cardData[0].data.referenceMessage && (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        background: "#ffffff20",
                                                        borderRadius: "16px",
                                                        padding: "10px",
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{ fontWeight: "bold", fontSize: "14px", color: "pink" }}
                                                    >
                                                        {message.cardData[0].data.referenceMessage.OnTap.FAI.type !== "user"
                                                            ? coachData.coachPlanInfo.coachName
                                                            : "You"}
                                                    </Typography>
                                                    {message.cardData[0].data.referenceMessage.image && (
                                                        <img
                                                            src={message.cardData[0].data.referenceMessage.image}
                                                            alt="Message Image"
                                                            style={{
                                                                alignSelf: "center",
                                                                width: "40px",
                                                                objectFit: "contain",
                                                                height: "40px",
                                                                borderRadius: "8px",
                                                                marginBottom: "8px",
                                                            }}
                                                        />
                                                    )}
                                                    {message.cardData[0].data.referenceMessage.text && (
                                                        <Typography sx={{ fontSize: "14px" }}>
                                                            {message.cardData[0].data.referenceMessage.text}
                                                        </Typography>
                                                    )}
                                                </div>
                                            )}
                                            {message.cardData[0].data.imageUrl && (
                                                <img
                                                    src={message.cardData[0].data.imageUrl}
                                                    alt="Message Image"
                                                    style={{
                                                        width: "100%",
                                                        borderRadius: "8px",
                                                        marginBottom: "8px",
                                                    }}
                                                />
                                            )}
                                            {message.cardData[0].data.text && (
                                                <div sx={{ fontSize: "14px" }}>{message.cardData[0].data.text}</div>
                                            )}
                                        </IncomingBubble>
                                    ) : (
                                        <OutgoingBubble>
                                            {message.cardData[0].data.referenceMessage && (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        background: "#ffffff20",
                                                        borderRadius: "16px",
                                                        padding: "10px",
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{ fontWeight: "bold", fontSize: "14px", color: "pink" }}
                                                    >
                                                        {message.cardData[0].data.referenceMessage.OnTap.FAI.type !== "user"
                                                            ? coachData.coachPlanInfo.coachName
                                                            : "You"}
                                                    </Typography>
                                                    {message.cardData[0].data.referenceMessage.image && (
                                                        <img
                                                            src={message.cardData[0].data.referenceMessage.image}
                                                            alt="Message Image"
                                                            style={{
                                                                alignSelf: "center",
                                                                width: "40px",
                                                                objectFit: "contain",
                                                                height: "40px",
                                                                borderRadius: "8px",
                                                                marginBottom: "8px",
                                                            }}
                                                        />
                                                    )}
                                                    {message.cardData[0].data.referenceMessage.text && (
                                                        <div sx={{ fontSize: "14px" }}>
                                                            {message.cardData[0].data.referenceMessage.text}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {message.cardData[0].data.imageUrl && (
                                                <img
                                                    src={message.cardData[0].data.imageUrl}
                                                    alt="Message Image"
                                                    style={{
                                                        width: "100%",
                                                        borderRadius: "8px",
                                                        marginBottom: "8px",
                                                    }}
                                                />
                                            )}
                                            {message.cardData[0].data.text && (
                                                <Typography sx={{ fontSize: "14px" }}>
                                                    {message.cardData[0].data.text}
                                                </Typography>
                                            )}
                                        </OutgoingBubble>
                                    )}
                                    <Typography
                                        sx={{
                                            fontSize: "10px",
                                            color: "#999",
                                            textAlign: message.senderType === "coach" ? "left" : "right",
                                        }}
                                    >
                                        {format(new Date(message.messageTimestamp), "hh:mm a")}
                                    </Typography>
                                </Box>
                            </SwipeableBubble>
                        ))}
                    </React.Fragment>
                ))}
                <div className="row-12" style={{ width: "100%" }} ref={messagesEndRef}></div>
            </Box>

            {/* Reply Preview */}
            {referenceMessage && (
                <div
                    style={{
                        backgroundColor: "#fff",
                        padding: "10px",
                        borderTop: "1px solid #ddd",
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <p
                            style={{
                                fontSize: "12px",
                                color: "pink",
                                margin: 0,
                                fontWeight: "bold",
                            }}
                        >
                            You
                            <button
                                style={{
                                    position: "absolute",
                                    right: "0",
                                    background: "none",
                                    border: "none",
                                    color: "black",
                                    fontSize: "16px",
                                    cursor: "pointer",
                                }}
                                onClick={() => setReferenceMessage(null)}
                            >
                                ✖
                            </button>
                        </p>
                        <div style={{ fontSize: "12px", display: "flex", flexDirection: "column" }}>
                            {referenceMessage.image && referenceMessage.image !== "" && (
                                <img
                                    src={referenceMessage.image}
                                    alt="Reply Preview"
                                    style={{
                                        alignContent: "left",
                                        objectFit: "contain",
                                        height: "50px",
                                        width: "50px",
                                        borderRadius: "8px",
                                        marginTop: "5px",
                                    }}
                                />
                            )}
                            {referenceMessage.text && referenceMessage.text !== "" && referenceMessage.text}
                        </div>
                    </div>
                </div>
            )}

            {/* Input Field */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px",
                    background: "linear-gradient(270deg, #fff 0%, rgba(255, 152, 0, 0.11) 70%)",
                    borderTop: "1px solid #ddd",
                }}
            >
                {/* Text Field for Message Input */}
                <TextField
                    variant="outlined"
                    size="small"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)} // Removed trim()
                    placeholder="Type a message"
                    sx={{
                        flex: 1,
                        marginRight: "10px",
                        backgroundColor: "white",
                        border: "2px solid #771615",
                        borderRadius: "20px",
                        ".MuiOutlinedInput-notchedOutline": {
                            border: "none",
                        },
                    }}
                />

                {/* Image Selection Button */}
                <IconButton
                    component="label"
                    sx={{
                        padding: "10px",
                        backgroundColor: "white",
                        "&:active": { backgroundColor: "#f0f0f0" },
                        borderRadius: "50%",
                        color: "#771615",
                        boxShadow: "0 2px 5px #771615",
                        marginRight: "10px",
                    }}
                >
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageSelect(e)}
                        hidden
                    />
                    <PhotoLibraryRounded />
                </IconButton>

                {/* Send Button */}
                <IconButton
                    onClick={() => {
                        sendMessage(false, "");
                    }}
                    sx={{
                        padding: "10px",
                        backgroundColor: "white",
                        "&:active": { backgroundColor: "#f0f0f0" },
                        borderRadius: "50%",
                        color: "#771615",
                        boxShadow: "0 2px 5px #771615",
                    }}
                >
                    <SendRounded />
                </IconButton>
            </Box>

            {/* Loading Screen */}
            {isLoading && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 10,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                    }}
                >
                    <LoadingScreenBlackLogo message={loadingMessage} />
                </Box>
            )}
        </Box>
    );
};

export default Fsn2CoachChat;